import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Search, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { matches } from "@/lib/search";
import { brl, num } from "@/lib/format";
import { NewDemoButton } from "@/components/master/DemoTools";
import { Button, Card, Chip, EmptyState, ErrorState, Input, Loading, Page, PageHeader } from "@/components/kit";

export const STATE = {
  ativo: { label: "Ativo", tone: "success" },
  teste: { label: "Em teste", tone: "brand" },
  vencido: { label: "Vencido", tone: "warning" },
  bloqueado: { label: "Bloqueado", tone: "danger" },
};
export const StateBadge = ({ state, ...p }) => <span className={`badge badge-${STATE[state]?.tone || "muted"}`} {...p}>{STATE[state]?.label || state}</span>;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("pt-BR") : "—");

function Kpi({ label, value, testid }) {
  return <Card data-testid={testid}><div className="caption">{label}</div><div className="h2 tnum mt-1">{value}</div></Card>;
}

function TenantRow({ t }) {
  const nav = useNavigate();
  const due = t.state === "teste" ? t.trial_until : t.paid_until;
  return (
    <tr className="border-t cursor-pointer hover:bg-[var(--brand-tint)]" style={{ borderColor: "var(--border)" }} onClick={() => nav(`/master/tenants/${t.id}`)} data-testid={`tenant-row-${t.slug}`}>
      <td className="py-3 pl-1"><div className="label">{t.name}</div><div className="caption">{t.is_demo && <span className="badge badge-warning mr-1.5" data-testid={`tenant-demo-badge-${t.slug}`}>Demo{t.demo_seed === "preparando" ? " · preparando" : t.demo_seed === "erro" ? " · falhou" : ""}</span>}{t.slug}{t.domains?.length ? ` · ${t.domains.join(", ")}` : ""}</div></td>
      <td><StateBadge state={t.state} data-testid={`tenant-state-${t.slug}`} />{t.auto_billing && <div className="caption mt-0.5" data-testid={`tenant-auto-${t.slug}`}>Cobrança automática{t.cancel_at_period_end ? " (encerrando)" : ""}</div>}</td>
      <td className="caption tnum">{fmtDate(due)}</td>
      <td className="text-right tnum">{t.usage.users}</td>
      <td className="text-right tnum">{t.usage.units}</td>
      <td className="text-right tnum">{t.usage.conferences}</td>
      <td className="caption tnum text-right">{t.usage.last_login_at ? new Date(t.usage.last_login_at).toLocaleString("pt-BR") : "nunca"}</td>
      <td className="text-right pr-1"><a href={t.access_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="icon-btn inline-flex" title="Abrir acesso" data-testid={`tenant-open-${t.slug}`}><ExternalLink size={14} /></a></td>
    </tr>
  );
}

function LegacyNotice({ legacy }) {
  if (!legacy?.units && !legacy?.users) return null;
  return <div className="rounded-xl p-4 text-sm mb-5 border" style={{ background: "var(--accent-soft)", borderColor: "var(--accent)" }} data-testid="legacy-notice">Existem dados anteriores à plataforma ({legacy.units} unidade(s), {legacy.users} usuário(s)) sem cliente. Eles ficam guardados e podem ser vinculados a um cliente na tela de detalhes dele.</div>;
}

export default function MasterHome() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [state, setState] = useState("all");
  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: ["master-overview"], queryFn: () => api("/master/overview") });
  const list = useMemo(() => (data?.tenants || []).filter((t) => (state === "all" || t.state === state) && matches(`${t.name} ${t.slug}`, q)), [data, q, state]);
  if (isLoading) return <Loading />;
  if (isError) return <Page><ErrorState message={error.message} onRetry={refetch} /></Page>;
  return (
    <Page>
      <PageHeader title="Clientes" subtitle="Restaurantes e revendas da plataforma" right={<div className="flex gap-2"><NewDemoButton /><Button icon={Plus} onClick={() => nav("/master/tenants/new")} data-testid="new-tenant-button">Novo cliente</Button></div>} />
      <LegacyNotice legacy={data.legacy} />
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Kpi label="Clientes" value={data.tenants.length} testid="kpi-tenants" />
        <Kpi label="Ativos" value={data.states.ativo || 0} testid="kpi-active" />
        <Kpi label="Receita mensal prevista" value={brl(data.mrr)} testid="kpi-mrr" />
        <Kpi label="Usuários na plataforma" value={num(data.totals.users, 0)} testid="kpi-users" />
        <Kpi label="Conferências registradas" value={num(data.totals.conferences, 0)} testid="kpi-conferences" />
      </div>
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]"><Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 muted" /><Input className="pl-10" placeholder="Buscar cliente" value={q} onChange={(e) => setQ(e.target.value)} data-testid="tenant-search-input" /></div>
        <div className="scroll-x">{["all", "ativo", "teste", "vencido", "bloqueado"].map((s) => <Chip key={s} active={state === s} onClick={() => setState(s)} data-testid={`tenant-filter-${s}`}>{s === "all" ? "Todos" : STATE[s].label}{s !== "all" && data.states[s] ? ` (${data.states[s]})` : ""}</Chip>)}</div>
      </div>
      {!data.tenants.length ? <EmptyState title="Nenhum cliente ainda" text="Crie o primeiro restaurante da plataforma · ele recebe marca, unidade, meta de CMV e o login do administrador na hora." action={<Button icon={Plus} onClick={() => nav("/master/tenants/new")} data-testid="empty-new-tenant-button">Criar cliente</Button>} /> : (
        <Card className="overflow-x-auto !p-3">
          <table className="w-full text-sm min-w-[860px]" data-testid="tenants-table">
            <thead><tr className="caption text-left"><th className="py-2 pl-1">Cliente</th><th>Status</th><th>Vence em</th><th className="text-right">Usuários</th><th className="text-right">Unidades</th><th className="text-right">Conferências</th><th className="text-right">Último acesso</th><th /></tr></thead>
            <tbody>{list.map((t) => <TenantRow key={t.id} t={t} />)}</tbody>
          </table>
          {!list.length && <p className="caption p-3">Nenhum cliente neste filtro.</p>}
        </Card>
      )}
    </Page>
  );
}
